import React from "react";
import {
  Banner,
  Button,
  Text,
  ScreenSizeRender,
  useScreenSize,
  Flex,
  Link,
} from "@jsq/ds/core";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { LoginCard } from "../../common/LoginCard";
import { UserContext } from "../../common/constants";
import { defaultSingleManagerPoweredByMessage } from "../../common/LoginCard/constants";
import commonStyles from "../../common/styles.scss";

type Props = {
  email: string;
  onClickBack: () => void;
  message?: string;
  error?: string;
  arena?: UnauthenticatedArenaObjectType;
  userContext: UserContext;
  onPasswordResetFAQClick: () => void;
  showPoweredByHeader?: boolean;
};

const ForgotPasswordInformationCard: React.FC<Props> = ({
  email,
  message,
  error,
  arena,
  onClickBack,
  userContext,
  onPasswordResetFAQClick,
  showPoweredByHeader = true,
}) => {
  const screenSize = useScreenSize();
  const isGlobalUser = userContext === UserContext.Global;

  return (
    <LoginCard
      className={commonStyles.Login__resetPasswordCard}
      showCardTitle
      showPoweredByHeader={showPoweredByHeader}
      poweredByHelpMessage={defaultSingleManagerPoweredByMessage(arena?.name)}
      cardTitle="Check your email for instructions"
      titleDataTestId="forgot-password-information-card-title"
      description={
        message || (
          <Flex flexDirection="column" gap={15}>
            <Text
              variant="secondary-md"
              align="left"
              data-testid="forgot-password-information-card-text-content1"
              data-cy="forgot-password-information-card-text1"
            >
              If <b>{email}</b> is associated with an account, you should receive an email
              containing a link to reset your password within the next 10 minutes.
            </Text>
            {isGlobalUser ? (
              <Text
                variant="secondary-md"
                align="left"
                data-testid="forgot-password-information-card-text-content2"
                data-cy="forgot-password-information-card-text2"
              >
                If you don&apos;t see an email, check your spam folder or try{" "}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  isInline
                  to=""
                  onClick={() => onClickBack()}
                  data-cy="forgot-password-information-card-reset-anchor"
                  data-testid="forgot-password-information-card-reset-anchor"
                >
                  resetting your password again
                </Link>
                . If you are still having issues, please reference our{" "}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  isInline
                  to=""
                  onClick={onPasswordResetFAQClick}
                  data-cy="forgot-password-information-card-faq-anchor"
                  data-testid="forgot-password-information-card-faq-anchor"
                >
                  password reset FAQ guide
                </Link>{" "}
                to help troubleshoot.
              </Text>
            ) : (
              <Text
                variant="secondary-md"
                align="left"
                data-testid="forgot-password-information-card-text-content2"
                data-cy="forgot-password-information-card-text2"
              >
                If you don&apos;t see an email, check your spam folder or try{" "}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  isInline
                  to=""
                  onClick={() => onClickBack()}
                  data-cy="forgot-password-information-card-reset-anchor"
                >
                  resetting your password again
                </Link>
                . If you are still having issues, contact us for further assistance.
              </Text>
            )}
          </Flex>
        )
      }
      descriptionDataTestId="forgot-password-information-card-text"
      onGoBackClick={screenSize === "small" ? onClickBack : undefined}
      goBackButtonText={screenSize === "small" ? "Go back" : undefined}
    >
      {onClickBack && (
        <ScreenSizeRender medium large>
          <Button
            icon="chevron-left"
            variant="muted"
            className={commonStyles.Login__backButton}
            onClick={onClickBack}
          >
            Back
          </Button>
        </ScreenSizeRender>
      )}
      {error && <Banner variant="danger" title="Error" description={error} />}
    </LoginCard>
  );
};

export default ForgotPasswordInformationCard;
